import React, { useContext } from 'react'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import UnauthorizedLayout from '../UnauthorizedLayout'
import CostsOverview from '../../Costs/CostsOverview'
import GoogleSVG from '../../svg/GoogleSVG'
import AppleSVG from '../../svg/AppleSVG'
import FormButton from '../../ui/FormButton'
import FormInput from '../../ui/FormInput'
import AuthService from '../../../services/AuthService'
import useNotification from '../../../hooks/useNotification'
import { resetSeenLevelsOnLogin } from '../../../contexts/OnboardingContext'

import AltLoginButton from './LoginItems/AltLoginButton'
import EvaluationBanner from './LoginItems/EvaluationBanner'
import useLicence from '../../../hooks/useLicence'
import { licenceBanner, licenceFromExpiredDetail } from '../../../config/licence'
import { SERVER_URL } from '../../../config/runtimeConfig'

// Id of the Info & costs section under the login form: the arrow, /costs and
// /login#costs all lead to it.
export const COSTS_SECTION_ID = 'costs'

const Login = ({ revealCosts = false }) => {
  const { t } = useTranslation('auth')
  const { t: tCosts } = useTranslation('costs')
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    resetField,
  } = useForm()

  const { info, error: showError } = useNotification()

  const [cookies, setCookie] = useCookies(['access_token_cookie'])
  const navigate = useNavigate()

  // Valutazione scaduta o licenza non valida: il backend rifiuterebbe il
  // login con un 403, qui si spiega il perche' e si disabilita il modulo.
  const [licence, setLicence] = useLicence()
  const { blocked: licenceBlocked } = licenceBanner(licence)

  const onSubmit = async (data) => {
    try {
      const response = await AuthService.login(data.email, data.password)

      // Check if MFA is required
      if (response.data.mfa_required) {
        sessionStorage.setItem('mfa_token', response.data.mfa_token)
        navigate('/mfa-challenge')
        return
      }

      const access_token = response.data.token

      // Check previous session (from sessionStorage, not localStorage for privacy)
      const previousEmail = sessionStorage.getItem('currentUserEmail')
      if (previousEmail && previousEmail !== data.email) {
        // Different user - clear all caches
        console.debug('[Login] User switched. Clearing session data...')
        sessionStorage.clear()
      }

      // Store current user email in sessionStorage (cleared on tab close)
      // NOT in localStorage to prevent persistent PII on shared devices
      sessionStorage.setItem('currentUserEmail', data.email)

      // Reset onboarding seen levels for fresh tour on login
      resetSeenLevelsOnLogin(data.email)

      // Set cookie with explicit options
      // Il cookie lo ha gia' messo la risposta del login (Set-Cookie
      // HttpOnly): riscriverlo da qui vorrebbe dire rinunciare a quel flag.

      // Force a hard refresh by using window.location.replace with a cache-busting parameter
      // This ensures all cached data is cleared and fresh data is loaded
      // The cookie will be available on reload since setCookie is synchronous
      const timestamp = Date.now()
      window.location.replace(`/?_t=${timestamp}`)
    } catch (e) {
      console.error('Login failed:', e?.response?.data)
      const expiredLicence = licenceFromExpiredDetail(e?.response?.data?.detail)
      if (expiredLicence) {
        setLicence(expiredLicence)
        resetField('password')
        return
      }
      const errorDetail =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.response?.data?.message

      // Check if password is not set (admin reset without setting password)
      if (errorDetail && errorDetail.includes('PASSWORD_NOT_SET')) {
        info(t('login.toast.passwordNotSet'))
        resetField('password')
        return
      }

      const errorMessage = errorDetail || t('login.toast.loginFailed')
      showError(errorMessage)
      resetField('password')
    }
  }

  const handleForgotPassword = () => {
    navigate('/password-reset')
  }

  const goToSignup = () => {
    navigate('/signup')
  }

  const handleGoogleLogin = () => {
    const baseUrl = SERVER_URL.replace(/\/api\/?$/, '').replace(/\/+$/, '')
    window.location.href = `${baseUrl}/api/auth/weresp/oauth/google`
  }

  const handleAppleLogin = () => {
    const baseUrl = SERVER_URL.replace(/\/api\/?$/, '').replace(/\/+$/, '')
    window.location.href = `${baseUrl}/api/auth/weresp/oauth/apple`
  }

  return (
    <UnauthorizedLayout
      question={t('login.question')}
      text={t('login.signupLink')}
      hancleClick={goToSignup}
      showPoweredBy={true}
      below={<CostsOverview id={COSTS_SECTION_ID} variant="landing" />}
      belowId={COSTS_SECTION_ID}
      belowHintLabel={tCosts('nav.infoAndCosts')}
      revealOnMount={revealCosts}
    >
      <div className="w-[100%] desktop:w-[500px] flex flex-col gap-4">
        <EvaluationBanner licence={licence} />
        <fieldset
          disabled={licenceBlocked}
          className={clsx('flex flex-col gap-4', { 'opacity-50': licenceBlocked })}
          data-testid="login-fieldset"
        >
          <form
            className="mt-6 flex flex-col gap-2"
            onSubmit={handleSubmit(onSubmit)}
            data-testid="login-form"
          >
            <FormInput
              label={t('labels.email')}
              name="email"
              type="text"
              placeholder={t('placeholders.email')}
              register={register}
              validationRules={{
                required: t('validation.emailRequired'),
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: t('validation.emailInvalid'),
                },
              }}
              errors={errors}
              testId="login-email-input"
            />
            <FormInput
              label={t('labels.password')}
              name="password"
              type="password"
              placeholder={t('placeholders.password')}
              register={register}
              validationRules={{ required: t('validation.passwordRequired') }}
              errors={errors}
              withPasswordToggle={true}
              onForgotPassword={handleForgotPassword}
              testId="login-password-input"
            />
            <FormButton
              isSubmitting={isSubmitting}
              text={t('login.button')}
              disabled={licenceBlocked}
              testId="login-submit-btn"
            />
          </form>
          <div
            className={clsx(
              'my-4 relative w-full bg-white bg-opacity-70 h-[0.5px]'
            )}
          >
            <p
              className={clsx(
                'absolute top-[-9px] taller:top-[-11px] left-1/2 translate-x-[-50%] bg-dark-blue text-xs taller:text-sm text-white text-opacity-70 font-light px-4'
              )}
            >
              {t('login.socialDivider')}
            </p>
          </div>
          <div className="grid grid-cols-1 items-center gap-2">
            <AltLoginButton
              text={t('login.google')}
              isSubmitting={isSubmitting}
              onClick={handleGoogleLogin}
            >
              <GoogleSVG />
            </AltLoginButton>
            {/* Apple OAuth temporarily disabled - pending Apple Developer Console configuration */}
            {/* <AltLoginButton
              text={t('login.apple')}
              isSubmitting={isSubmitting}
              onClick={handleAppleLogin}
            >
              <AppleSVG />
            </AltLoginButton> */}
          </div>
        </fieldset>
      </div>
    </UnauthorizedLayout>
  )
}

export default Login
